// apps/access-control/access-control-admin/src/app/Layout/AppLayout.tsx
import { Layout, theme as antdTheme, Breadcrumb, Alert, Button, Tooltip } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { MoonOutlined, QuestionCircleOutlined, SunOutlined, UserOutlined } from '@ant-design/icons';
import { logger } from '@digiwedge/logger';
import {
  createSidebarViewportStyle,
  HeaderComponent,
  SidebarComponent,
  usePersistentSidebarOpenKeys,
} from '@digiwedge/ui-antd';
import { NotificationCenter } from '@digiwedge/ui-access-control';
import { DocsLinkButton } from '@digiwedge/ui-docs-link';
import { CookieSettingsLink } from '@digiwedge/cmp-consent-react';
import { DOCS_CONFIG } from '../../utils/help-links.js';
import type { MenuProps } from 'antd';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import type { CapabilityReason } from '../hooks/useCapabilities.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { readEnvVar } from '../../env.runtime.js';

const { Content } = Layout;
const TENANT_NAV_OPEN_KEYS = ['tenants-group'];
const NAV_OPEN_KEYS_STORAGE_KEY = 'access-control-admin:sidebar-open-keys';

type NavItems = NonNullable<MenuProps['items']>;

/** Friendly labels for URL segments that don't title-case correctly */
const SEGMENT_LABELS: Record<string, string> = {
  voicepro: 'VoicePro',
  scim: 'SCIM Provisioning',
  saml: 'SAML Configuration',
};

function segmentLabel(seg: string): string {
  return SEGMENT_LABELS[seg] ?? seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Build a sidebar/nav label with a capability tooltip */
function capLabel(
  label: string,
  allowed: boolean,
  capKey: string,
  reasons?: Record<string, CapabilityReason>,
): React.ReactNode {
  const title = allowed
    ? reasons?.[capKey] === 'bypass'
      ? 'Allowed (bypass)'
      : undefined
    : `Requires ${capKey} capability`;
  return <span title={title}>{label}</span>;
}

export interface AppLayoutProps {
  children?: ReactNode;
  toggleTheme?: () => void;
  themeMode?: 'light' | 'dark';
  defaultCollapsed?: boolean;
  /** Hide the sidebar for public/minimal pages */
  hideSidebar?: boolean;
  /** Hide the breadcrumbs row */
  hideBreadcrumbs?: boolean;
  /** Optional override for header nav items */
  navItems?: NonNullable<MenuProps['items']>;
  /** Optional override for user menu items */
  userMenuItems?: NonNullable<MenuProps['items']>;
  /** Optional live notification count supplied by a tenant-scoped inbox contract. */
  notificationsCount?: number;
}

export function AppLayout({
  toggleTheme,
  themeMode = 'light',
  defaultCollapsed = false,
  hideSidebar = false,
  hideBreadcrumbs = false,
  navItems,
  userMenuItems,
  notificationsCount,
}: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const profile = useGlobalStore((s: GlobalState) => s.profile);
  const navigate = useNavigate();
  const location = useLocation();
  const caps = useCapabilities();

  // Extract tenant ID from path for contextual sub-navigation (SCIM/SAML)
  const tenantMatch = location.pathname.match(/^\/tenants\/([^/]+)/);
  const activeTenantId = tenantMatch && tenantMatch[1] !== 'create' ? tenantMatch[1] : null;
  const defaultSidebarOpenKeys = activeTenantId ? TENANT_NAV_OPEN_KEYS : [];
  const [sidebarOpenKeys, setSidebarOpenKeys] = usePersistentSidebarOpenKeys({
    storageKey: NAV_OPEN_KEYS_STORAGE_KEY,
    defaultOpenKeys: defaultSidebarOpenKeys,
    validOpenKeys: TENANT_NAV_OPEN_KEYS,
  });
  const token = useGlobalStore((s: GlobalState) => s.token);
  const isAuthenticated = useGlobalStore((s: GlobalState) => s.isAuthenticated);
  const canViewUserProfiles = caps.canUserRead;

  const computedUserMenuItems = useMemo<NonNullable<MenuProps['items']>>(() => {
    if (userMenuItems) {
      return userMenuItems;
    }

    const menuItems: NonNullable<MenuProps['items']> = [
      ...(canViewUserProfiles ? [{ label: 'User Profiles', key: '/user-profiles' }] : []),
      { label: 'Settings', key: '/settings' },
      { label: 'Logout', key: '/logout' },
    ];

    return menuItems;
  }, [canViewUserProfiles, userMenuItems]);

  const computedNavItems: NavItems = useMemo(() => {
    if (navItems) {
      return navItems;
    }

    // Keep top-nav concise; sidebar remains the full navigation surface.
    // Items are always shown (disabled when lacking capability) so admins
    // know the surface exists even when they lack permission.
    const adminItems: NavItems = [
      {
        label: capLabel('Tenants', caps.canTenantRead, 'TENANT_MGMT:read', caps.reasons),
        key: '/tenants',
        disabled: !caps.canTenantRead,
      },
      {
        label: capLabel('Users', caps.canUserRead, 'USER_MGMT:read', caps.reasons),
        key: '/users',
        disabled: !caps.canUserRead,
      },
      {
        label: capLabel('Roles', caps.canRoleRead, 'ROLE_MGMT:read', caps.reasons),
        key: '/roles',
        disabled: !caps.canRoleRead,
      },
      {
        label: capLabel(
          'Permissions',
          caps.canPermissionRead,
          'PERMISSION_MGMT:read',
          caps.reasons,
        ),
        key: '/permissions',
        disabled: !caps.canPermissionRead,
      },
      {
        label: capLabel('Features', caps.canFeatureRead, 'FEATURE_MGMT:read', caps.reasons),
        key: '/features',
        disabled: !caps.canFeatureRead,
      },
      {
        label: capLabel(
          'Feature Flags',
          caps.canFeatureFlagRead,
          'FEATURE_FLAG:read',
          caps.reasons,
        ),
        key: '/feature-flags',
        disabled: !caps.canFeatureFlagRead,
      },
      {
        label: capLabel(
          'Invitations',
          caps.canInvitationRead,
          'INVITATION_MGMT:read',
          caps.reasons,
        ),
        key: '/invitations',
        disabled: !caps.canInvitationRead,
      },
    ];

    return [
      { label: 'Dashboard', key: '/dashboard' },
      { label: 'Admin', key: 'admin', children: adminItems },
      { label: 'Health', key: '/health' },
    ] as NavItems;
  }, [
    navItems,
    caps.canTenantRead,
    caps.canUserRead,
    caps.canRoleRead,
    caps.canPermissionRead,
    caps.canFeatureRead,
    caps.canFeatureFlagRead,
    caps.canInvitationRead,
    caps.reasons,
  ]);

  const selectedNavKey: string | undefined = useMemo(() => {
    const p = location.pathname;
    if (p === '/') return '/';
    if (p.startsWith('/dashboard')) return '/dashboard';
    if (p.startsWith('/health')) return '/health';
    if (p.startsWith('/tenants')) return '/tenants';
    if (p.startsWith('/users')) return '/users';
    if (p.startsWith('/roles')) return '/roles';
    if (p.startsWith('/permissions')) return '/permissions';
    if (p.startsWith('/features')) return '/features';
    if (p.startsWith('/feature-flags')) return '/feature-flags';
    if (p.startsWith('/invitations')) return '/invitations';
    if (p.startsWith('/settings')) return '/settings';
    return undefined;
  }, [location.pathname]);

  const handleCollapse = useCallback((value: boolean) => {
    logger.debug('Sidebar collapsed state', { value });
    setCollapsed(value);
  }, []);

  // Extract tokens from antd for consistent theming
  const { token: antdToken } = antdTheme.useToken();
  const themeToken = antdToken;

  const computeWsUrl = (): string | undefined => {
    try {
      const base = readEnvVar('VITE_ACCESS_CONTROL_BASE_URL') ?? '';
      if (!base) return '/ws/events';
      const u = new URL(base);
      u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
      u.pathname = '/ws/events';
      u.search = '';
      u.hash = '';
      return u.toString();
    } catch {
      return '/ws/events';
    }
  };

  // Session expiry warning (cookie-refresh enabled, but surface UX hint when access token is near expiry)
  const [ttlSec, setTtlSec] = useState<number | null>(null);
  const warningThresholdSec = 5 * 60; // 5 minutes
  const claims = useMemo(() => {
    try {
      if (!token) return null;
      const [, payload] = token.split('.');
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const json = JSON.parse(typeof atob === 'function' ? atob(base64) : '') as {
        exp?: number;
        iat?: number;
      };
      return json;
    } catch {
      return null;
    }
  }, [token]);

  useEffect(() => {
    const tick = () => {
      try {
        if (!claims?.exp) {
          setTtlSec(null);
          return;
        }
        const now = Math.floor(Date.now() / 1000);
        setTtlSec(Math.max(0, claims.exp - now));
      } catch {
        setTtlSec(null);
      }
    };
    tick();
    const timerLocal = setInterval(tick, 30_000);
    return () => {
      if (timerLocal) clearInterval(timerLocal);
    };
  }, [claims]);

  return (
    <Layout
      style={{
        minHeight: '100vh',
        background: themeToken.colorBgLayout,
        border: 'none',
      }}
    >
      {/* Header */}
      <HeaderComponent
        navItems={computedNavItems}
        selectedNavKey={selectedNavKey}
        userMenuItems={computedUserMenuItems}
        userProfilePath={canViewUserProfiles ? '/user-profiles' : undefined}
        notificationsCount={isAuthenticated ? notificationsCount : undefined}
        themeMode={themeMode}
        buttonColor={themeMode === 'dark' ? '#6aae43' : undefined}
        user={profile ? { name: profile.name, avatarUrl: profile.avatarUrl } : undefined}
        rightExtra={
          <>
            {isAuthenticated && canViewUserProfiles ? (
              <Tooltip title="User profiles">
                <Button
                  type="text"
                  icon={<UserOutlined />}
                  onClick={() => navigate('/user-profiles')}
                  aria-label="Open user profiles"
                  style={{
                    color: themeToken.colorText,
                  }}
                />
              </Tooltip>
            ) : null}
            <DocsLinkButton
              pathname={location.pathname}
              config={DOCS_CONFIG}
              icon={<QuestionCircleOutlined />}
            />
            {toggleTheme ? (
              <Tooltip title={themeMode === 'dark' ? 'Light mode' : 'Dark mode'}>
                <Button
                  type="text"
                  icon={themeMode === 'dark' ? <SunOutlined /> : <MoonOutlined />}
                  onClick={toggleTheme}
                  aria-label={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                  style={{
                    color: themeToken.colorText,
                  }}
                />
              </Tooltip>
            ) : null}
            {/* Clicking the bell opens notifications route for now until a richer list is added */}
            {isAuthenticated ? (
              <button
                type="button"
                onClick={() => navigate('/notifications')}
                aria-label="Open notifications"
                style={{
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  margin: 0,
                  color: themeToken.colorText,
                  cursor: 'pointer',
                }}
              >
                <NotificationCenter themeMode={themeMode} wsUrl={computeWsUrl()} />
              </button>
            ) : null}
          </>
        }
      />

      {/* NotificationCenter is now rendered in the header via rightExtra */}

      <Layout style={{ flex: 1, background: themeToken.colorBgLayout }}>
        {/* Sidebar */}
        {!hideSidebar && (
          <SidebarComponent
            collapsed={collapsed}
            onCollapse={handleCollapse}
            themeMode={themeMode}
            sidebarItems={[
              { key: '/dashboard', label: 'Dashboard' },
              { key: '/health', label: 'Health' },
              // Tenants: show SCIM/SAML sub-nav when viewing a specific tenant
              ...(activeTenantId
                ? [
                    {
                      key: 'tenants-group',
                      label: capLabel(
                        'Tenants',
                        caps.canTenantRead,
                        'TENANT_MGMT:read',
                        caps.reasons,
                      ),
                      disabled: !caps.canTenantRead,
                      children: [
                        { key: '/tenants', label: 'All Tenants' },
                        { key: `/tenants/${activeTenantId}/edit`, label: 'Edit Tenant' },
                        { key: `/tenants/${activeTenantId}/scim`, label: 'SCIM Provisioning' },
                        { key: `/tenants/${activeTenantId}/saml`, label: 'SAML Configuration' },
                      ],
                    },
                  ]
                : [
                    {
                      key: '/tenants',
                      label: capLabel(
                        'Tenants',
                        caps.canTenantRead,
                        'TENANT_MGMT:read',
                        caps.reasons,
                      ),
                      disabled: !caps.canTenantRead,
                    },
                  ]),
              {
                key: '/onboarding/voicepro',
                label: capLabel(
                  'VoicePro Onboarding',
                  caps.canTenantCreate,
                  'TENANT_MGMT:create',
                  caps.reasons,
                ),
                disabled: !caps.canTenantCreate,
              },
              {
                key: '/user-profiles',
                label: capLabel('User Profiles', caps.canUserRead, 'USER_MGMT:read', caps.reasons),
                disabled: !caps.canUserRead,
              },
              {
                key: '/users',
                label: capLabel('Users', caps.canUserRead, 'USER_MGMT:read', caps.reasons),
                disabled: !caps.canUserRead,
              },
              {
                key: '/roles',
                label: capLabel('Roles', caps.canRoleRead, 'ROLE_MGMT:read', caps.reasons),
                disabled: !caps.canRoleRead,
              },
              {
                key: '/permissions',
                label: capLabel(
                  'Permissions',
                  caps.canPermissionRead,
                  'PERMISSION_MGMT:read',
                  caps.reasons,
                ),
                disabled: !caps.canPermissionRead,
              },
              {
                key: '/permission-assignments',
                label: capLabel(
                  'Permission Assignments',
                  caps.canPermissionAssignmentRead,
                  'PERMISSION_ASSIGNMENT:read',
                  caps.reasons,
                ),
                disabled: !caps.canPermissionAssignmentRead,
              },
              {
                key: '/features',
                label: capLabel('Features', caps.canFeatureRead, 'FEATURE_MGMT:read', caps.reasons),
                disabled: !caps.canFeatureRead,
              },
              {
                key: '/feature-flags',
                label: capLabel(
                  'Feature Flags',
                  caps.canFeatureFlagRead,
                  'FEATURE_FLAG:read',
                  caps.reasons,
                ),
                disabled: !caps.canFeatureFlagRead,
              },
              {
                key: '/consents',
                label: capLabel('Consents', caps.canConsentRead, 'CONSENT_MGMT:read', caps.reasons),
                disabled: !caps.canConsentRead,
              },
              {
                key: '/sessions',
                label: capLabel('Sessions', caps.canSessionRead, 'SESSION_MGMT:read', caps.reasons),
                disabled: !caps.canSessionRead,
              },
              {
                key: '/login-attempts',
                label: capLabel(
                  'Login Attempts',
                  caps.canLoginAttemptsRead,
                  'LOGIN_ATTEMPTS:read',
                  caps.reasons,
                ),
                disabled: !caps.canLoginAttemptsRead,
              },
              {
                key: '/invitations',
                label: capLabel(
                  'Invitations',
                  caps.canInvitationRead,
                  'INVITATION_MGMT:read',
                  caps.reasons,
                ),
                disabled: !caps.canInvitationRead,
              },
              { key: '/settings', label: 'Settings' },
            ]}
            openKeys={sidebarOpenKeys}
            onOpenChange={setSidebarOpenKeys}
            defaultOpenKeys={defaultSidebarOpenKeys}
            selectedKeys={[
              (() => {
                const p = location.pathname;
                // Exact match for tenant sub-pages (SCIM/SAML/edit)
                if (
                  activeTenantId &&
                  (p.endsWith('/scim') || p.endsWith('/saml') || p.endsWith('/edit'))
                )
                  return p;
                // Prefix match (longest first to avoid /permissions matching /permission-assignments)
                const prefixes = [
                  '/onboarding/voicepro',
                  '/permission-assignments',
                  '/user-profiles',
                  '/feature-flags',
                  '/login-attempts',
                  '/permissions',
                  '/invitations',
                  '/features',
                  '/consents',
                  '/sessions',
                  '/settings',
                  '/tenants',
                  '/health',
                  '/users',
                  '/roles',
                ];
                for (const prefix of prefixes) {
                  if (p.startsWith(prefix)) return prefix;
                }
                return '/dashboard';
              })(),
            ]}
            onClick={({ key }) => navigate(String(key))}
            style={createSidebarViewportStyle()}
          />
        )}

        <Layout style={{ background: themeToken.colorBgLayout }}>
          {!hideBreadcrumbs && (
            <Breadcrumb
              style={{ margin: '16px 0' }}
              items={location.pathname
                .split('/')
                .filter(Boolean)
                .map((seg, idx, arr) => ({
                  title: segmentLabel(seg),
                  href: '/' + arr.slice(0, idx + 1).join('/'),
                }))}
            />
          )}

          {/* Main Content */}
          <Content
            style={{
              padding: themeToken.padding,
              minHeight: 280,
              background: 'transparent',
            }}
          >
            {typeof ttlSec === 'number' && ttlSec <= warningThresholdSec && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={
                  ttlSec > 0
                    ? `Session will refresh soon (token expires in ~${Math.max(0, Math.floor(ttlSec / 60))}m).`
                    : 'Refreshing session…'
                }
              />
            )}
            <Outlet />
          </Content>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 16,
              padding: 16,
              color: themeToken.colorTextSecondary,
            }}
          >
            <span>© {new Date().getFullYear()} DigiWedge. All rights reserved.</span>
            <CookieSettingsLink
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: themeToken.colorTextSecondary,
                fontSize: 18,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
              }}
              aria-label="Cookie settings"
            >
              <span role="img" aria-label="Cookie settings">
                🍪
              </span>
            </CookieSettingsLink>
          </div>
        </Layout>
      </Layout>
    </Layout>
  );
}

export default AppLayout;
